import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'
import { graphqlFetch, GraphqlFetchOutput } from '@pylot-data/graphqlFetch'
import type { Cart } from '@pylot-data/pylotschema'
import { removeItemFromCartMutation } from './graphql/removeItemFromCartMutation'
import { CartData, useCart } from './use-cart'
import { getIsSignedIn } from './utils/getIsSignedIn'

export type RemoveItemFromCartInput = {
    input: {
        cart_id: string
        cart_item_uid: string
    }
    isSignedIn: boolean
}

export type RemoveItemFromCartOutput = {
    removeItemFromCart: {
        cart: Cart
    }
}

export type RemoveItemFromCart = (cartItemUid: string) => Promise<void>
export interface UseRemoveItemFromCartReturn {
    isLoading: boolean
    removeItemFromCart: RemoveItemFromCart
}

const formatData = (
    output: GraphqlFetchOutput<RemoveItemFromCartOutput>
): CartData => {
    return { data: { cart: output.data.removeItemFromCart?.cart } }
}

const getOptimisticData = (
    cartData: CartData,
    cartItemUid: string
): CartData => {
    const items = [...(cartData.data.cart.items || [])]
    const itemIndex = items.findIndex((item) => item?.uid === cartItemUid)
    items.splice(itemIndex, 1)

    return {
        data: {
            cart: {
                ...cartData.data.cart,
                items
            },
            isOptimistic: true
        }
    }
}

export const useRemoveItemFromCart = (): UseRemoveItemFromCartReturn => {
    const { locale } = useRouter()
    const { data, mutate } = useCart()
    const [isLoading, setIsLoading] = useState(false)

    return {
        isLoading,
        removeItemFromCart: useCallback(
            async (cartItemUid: string) => {
                setIsLoading(true)
                const optimisticData = getOptimisticData(data!, cartItemUid)
                mutate(optimisticData, false)

                await mutate(
                    graphqlFetch<
                        RemoveItemFromCartInput,
                        RemoveItemFromCartOutput
                    >({
                        query: removeItemFromCartMutation,
                        variables: {
                            input: {
                                cart_id: String(data!.data.cart.id!),
                                cart_item_uid: cartItemUid
                            },
                            isSignedIn: getIsSignedIn()
                        },
                        locale
                    }).then((res) => {
                        setIsLoading(false)
                        return formatData(res)
                    }),
                    false
                )
            },
            // TODO: check if data from hook changes every time
            [data, mutate]
        )
    }
}
